import React, { useState, useEffect } from "react";
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
    InputLabel, MenuItem, Select, styled, Paper, Typography,
    IconButton, CircularProgress, List, ListItem, ListItemText, Divider,
    TextField as MuiTextField,
    Chip, Button as MuiButton
} from "@mui/material";
import {
    List as RaList, Datagrid, TextField, TextInput, SelectInput, Button, useNotify,
    useDataProvider, DateField, FunctionField, TopToolbar, useListContext, SelectField,
    useRefresh,
} from "react-admin";
import { Download, Refresh } from "@mui/icons-material";
import dayjs from 'dayjs';
import { RunningVolumeDialog } from "./RunningVolumeDialog";
import DeployFilter from "./deployFilter";
import PasswordVerifiers from "./passwordVerifiers";

const permanentFilter = { trafficStatusList: [0, 1, 2, 3, 6] };


// 统一按钮样式
const tableButtonStyle = {
    whiteSpace: 'nowrap',
    color: '#1976d2',
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.04)'
    }
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        minWidth: 400,
        padding: theme.spacing(2),
    },
}));

const LogDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        width: '80%',
        maxWidth: 1000,
        maxHeight: '80vh',
        borderRadius: 12,
        boxShadow: theme.shadows[5],
        overflow: 'hidden',
    },
}));

const LogContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 12,
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const LogHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const LogItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: theme.palette.grey[50],
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        boxShadow: theme.shadows[1],
    },
}));

const StatusBadge = styled(Chip)(({ status }: any) => ({
    fontWeight: 'bold',
    backgroundColor: status === 'ERROR' ? '#ffebee' :
        status === 'WARN' ? '#fff8e1' :
            status === 'INFO' ? '#e3f2fd' : '#f3e5f5',
    color: status === 'ERROR' ? '#d32f2f' :
        status === 'WARN' ? '#ed6c02' :
            status === 'INFO' ? '#0288d1' : '#7b1fa2',
}));

const DownloadButton = styled(MuiButton)(({ theme }) => ({
    backgroundColor: '#2775EC',
    color: '#fff',
    padding: '6px 16px',
    borderRadius: 6,
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#1c5fc4',
    },
    '& .MuiSvgIcon-root': {
        marginRight: theme.spacing(1),
    },
}));

const trafficStatusTypes = [
    { id: 0, name: '部署初始化' },
    { id: 1, name: '部署完成' },
    { id: 2, name: '部署失败' },
    { id: 3, name: '部署超时' },
    // { id: 4, name: '未运行' },
    // { id: 5, name: '运行中' },
    { id: 6, name: '运行失败' }
];


const ListActions = () => {
    const { setFilters, filterValues } = useListContext();
    const dataProvider = useDataProvider();
    const [runDialogOpen, setRunDialogOpen] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    const [strategyCustomers, setStrategyCustomers] = useState([]);
    const [trafficCustomers, setTrafficCustomers] = useState([]);
    const [trafficCustomerId, setTrafficCustomerId] = useState('');
    const [strategyId, setStrategyId] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false); // 重命名为dialogOpen避免命名冲突
    const notify = useNotify();
    const [count, setCount] = useState(''); // 设备数量
    const [countError, setCountError] = useState(''); // 数量错误信息

    const [availableNumber, setAvailableNumber] = useState(0);

    const refresh = useRefresh(); // 获取刷新函数

    const validateCount = () => {
        if (!count) {
            setCountError('请输入需要部署流量的数量');
            return false;
        }

        const num = parseInt(count, 10);
        if (isNaN(num) || num <= 0) {
            setCountError('设备数量必须是正整数');
            return false;
        }

        if (num > availableNumber) {
            setCountError('设备数量不能大于可部署数量');
            return false;
        }


        setCountError('');
        return true;
    };


    const handleOpenRunDialog = () => {
        setRunDialogOpen(true);
    };

    const handleCloseRunDialog = () => {
        setRunDialogOpen(false);
    };


    const handleStart = async () => {
        let name = ''
        trafficCustomers.map((item: any) => {
            if (item.id == trafficCustomerId) {
                name += item.name
            }
        })
        strategyCustomers.map((item: any) => {
            if (item.id == strategyId) {
                name += item.name
            }
        })

        if (window.confirm(`确定通过${name}开始运行？`)) {
            const formData = {
                ...filterValues,
                count: parseInt(count, 10),
                deviceSns: filterValues.sns.split(/[,，]+/)
                    .map((sn: any) => sn.trim())
                    .filter((sn: any) => sn.length > 0),
                trafficCustomerId,
                strategyId
            };
            dataProvider.startRunningVolume(formData)
                .then(() => {
                    notify('开始流量成功', { type: 'success' });
                    refresh(); // 刷新列表数据
                    handleCloseRunDialog();
                })
                .catch((error: any) => {
                    notify(error.message || '开始流量失败', { type: 'error' });
                    refresh(); // 刷新列表数据
                });
        }

    };


    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const [trafficData, strategyData] = await Promise.all([
                    dataProvider.getTrafficCustomers(),
                    dataProvider.getStrategyList()
                ]);
                setTrafficCustomers(
                    trafficData
                        .filter((item: any) => !item.code)
                        .map((item: any) => ({ id: item.id, name: item.name }))
                );
                setStrategyCustomers(strategyData.map((item: any) => ({ id: item.id, name: item.name })));
            } catch (error) {
                console.error("获取客户数据失败:", error);
            }
        };
        if (runDialogOpen) {
            setTrafficCustomerId('');
            setCountError('');
            setCount('');
            setAvailableNumber(0);
            setStrategyId('');
            fetchCustomers();
        }
    }, [runDialogOpen]);

    const handleResetFilters = () => {
        setFilters({ ...permanentFilter });
    };

    const handleStopTraffic = async () => {
        const isCountValid = validateCount();
        if (!isCountValid) return;
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
    };

    const hasUserFilters = () => {
        // 排除默认的 trafficStatusList 字段，检查其他字段是否有值
        const userFilterKeys = Object.keys(filterValues).filter(key =>
            key !== 'trafficStatusList' && filterValues[key] != null && filterValues[key] !== ''
        );
        return userFilterKeys.length > 0;
    };

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const countData = await dataProvider.trafficCount({
                    salesCustomerIds: filterValues.saleCustomerIds,
                    deviceSns: filterValues.sns ? filterValues.sns.split(/[,，]+/)
                        .map((sn: any) => sn.trim())
                        .filter((sn: any) => sn.length > 0) : [],
                    strategyId,
                    trafficCustomerId
                });
                setAvailableNumber(countData.data);
            } catch (error) {
                console.error("获取数据失败:", error);
            }
        };

        if (strategyId && trafficCustomerId) {
            fetchCustomers();
        } else {
            setAvailableNumber(0);
        }

    }, [strategyId, trafficCustomerId]);


    return (
        <TopToolbar>
            <Button
                label="重置搜索"
                onClick={handleResetFilters}
                disabled={!hasUserFilters()}
            />
            <Button
                label="开始流量"
                onClick={handleOpenRunDialog}
                size="small"
            />
            <Button label="查看流量结果" onClick={() => setDialogOpen(true)}>
            </Button>
            <RunningVolumeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            />

            <StyledDialog open={runDialogOpen} onClose={handleCloseRunDialog}>
                <DialogTitle>选择流量参数</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="traffic-customer-label">流量商家</InputLabel>
                        <Select
                            labelId="traffic-customer-label"
                            value={trafficCustomerId}
                            label="流量商家"
                            onChange={(e) => setTrafficCustomerId(e.target.value)}
                        >
                            <MenuItem value="">请选择流量商家</MenuItem>
                            {trafficCustomers.map((customer: any) => (
                                <MenuItem key={customer.id} value={customer.id}>
                                    {customer.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="strategy-label">流量策略</InputLabel>
                        <Select
                            labelId="strategy-label"
                            value={strategyId}
                            label="流量策略"
                            onChange={(e) => setStrategyId(e.target.value)}
                        >
                            <MenuItem value="">请选择流量策略</MenuItem>
                            {strategyCustomers.map((strategy: any) => (
                                <MenuItem key={strategy.id} value={strategy.id}>
                                    {strategy.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <text>可部署数量:{availableNumber}</text>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <MuiTextField
                            label="部署数量"
                            type="number"
                            value={count}
                            onChange={(e) => setCount(e.target.value)}
                            onBlur={validateCount}
                            error={!!countError}
                            helperText={countError}
                            inputProps={{
                                min: 1,
                                step: 1
                            }}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRunDialog} color="inherit">取消</Button>
                    <Button
                        onClick={handleStopTraffic}
                        color="primary"
                        variant="contained"
                        disabled={!trafficCustomerId || !strategyId}
                    >
                        开始流量
                    </Button>
                </DialogActions>
            </StyledDialog>
            <PasswordVerifiers
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                onConfirm={handleStart}
                title="开始流量操作密码"
                confirmLabel="开始流量"
            />
        </TopToolbar>
    );
};

export const deployList = () => {
    const dataProvider = useDataProvider();
    const [logDialogOpen, setLogDialogOpen] = useState(false);
    const [currentLogs, setCurrentLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [deviceInfo, setDeviceInfo] = useState(null);


    const notify = useNotify();


    const handleViewLog = async (record: any) => {
        setLoadingLogs(true);
        setLogDialogOpen(true);
        setDeviceInfo({
            sn: record.sn,
            id: record.id,
            location: record.city,
            status: record.deviceStatus === 1 ? '在线' : '离线'
        });

        try {
            const response = await dataProvider.getLogInfo(record.id);
            if (response.code === 0 && Array.isArray(response.data)) {
                setCurrentLogs(response.data);
            } else {
                setCurrentLogs([]);
                notify(response.msg || '获取日志数据为空', { type: 'warning' });
            }
        } catch (error) {
            console.error("获取日志失败:", error);
            setCurrentLogs([]);
            notify('获取日志失败，请稍后重试', { type: 'error' });
        } finally {
            setLoadingLogs(false);
        }
    };

    const clickOpsLog = async (id) => {
        try {
            await dataProvider.opsLogInfo(id);
            notify('请求下发加载设备日志成功', { type: 'success' });
        } catch (error) {
            console.error("获取日志失败:", error);
            notify('请求下发日志失败', { type: 'error' });
        }
    };

    const formatTimestamp = (timestamp: any) => {
        if (!timestamp) return '';
        return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

    const handleRefreshLogs = async () => {
        if (!deviceInfo) return;

        setLoadingLogs(true);
        try {
            const response = await dataProvider.getLogInfo(deviceInfo.id);
            if (response.code === 0 && Array.isArray(response.data)) {
                setCurrentLogs(response.data);
                notify('日志已刷新', { type: 'success' });
            } else {
                setCurrentLogs([]);
                notify(response.msg || '获取日志数据为空', { type: 'warning' });
            }
        } catch (error) {
            console.error("刷新日志失败:", error);
            notify('刷新日志失败，请稍后重试', { type: 'error' });
        } finally {
            setLoadingLogs(false);
        }
    };


    return (
        <>
            <RaList
                filters={<DeployFilter />}
                actions={<ListActions />}
                empty={false}
            >
                <Datagrid bulkActionButtons={false}>
                    <TextField source="id" label="序号" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="sn" label="设备SN" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <DateField source="activateTime" label="用户绑定日期" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <FunctionField
                        label="设备位置"
                        source="city"
                        render={record =>
                            (record.province || '').replace(/省$/, '') +
                            ' ' +
                            (record.city || '').replace(/市$/, '')
                        }
                        sortable={false}
                    />
                    <FunctionField
                        label="设备状态"
                        source="deviceStatus"
                        render={record => record.deviceStatus == 1 ? '在线' : '离线'}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />
                    <FunctionField
                        label="上行网速"
                        source="uploadSpeed"
                        render={record => (record.uploadSpeed || 0) + 'Mbps'}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />

                    <TextField source="softwareVersion" label="软件版本" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="firmwareVersion" label="固件版本" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="natType" label="NAT类型" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="operator" label="运营商" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="deviceTag" label="设备特殊标签" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="saleCustomerName" label="设备商家" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <SelectField source="trafficStatus" label="运行状态" choices={trafficStatusTypes} sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <FunctionField
                        label="操作"
                        render={(record) => (
                            <Box sx={{ justifyContent: 'center', display: 'flex',gap: '8px' }}>
                                <Button
                                    label="上报日志"
                                    onClick={() => clickOpsLog(record.id)}
                                    sx={tableButtonStyle}
                                    disabled={record.deviceStatus != 1}
                                />
                                <Button
                                    label="查看日志"
                                    onClick={() => handleViewLog(record)}
                                    sx={tableButtonStyle}
                                />
                            </Box>
                        )}
                    />
                </Datagrid>
            </RaList>

            {/* 日志查看对话框 */}
            <LogDialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" component="div">设备操作日志</Typography>
                    <Box>
                        <IconButton onClick={handleRefreshLogs} disabled={loadingLogs} title="刷新日志">
                            <Refresh />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {deviceInfo && (
                        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip label={`SN: ${deviceInfo.sn}`} variant="outlined" />
                            <Chip label={`ID: ${deviceInfo.id}`} variant="outlined" />
                            <Chip label={`位置: ${deviceInfo.location}`} variant="outlined" />
                            <Chip
                                label={`状态: ${deviceInfo.status}`}
                                variant="outlined"
                                color={deviceInfo.status === '在线' ? 'success' : 'error'}
                            />
                        </Box>
                    )}

                    {loadingLogs ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                            <Typography variant="body1" sx={{ ml: 2 }}>正在加载日志信息...</Typography>
                        </Box>
                    ) : currentLogs.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="body1">暂无日志数据</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                请尝试点击"上报日志"按钮或刷新页面
                            </Typography>
                        </Box>
                    ) : (
                        <LogContainer>
                            <LogHeader>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    日志文件列表
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    共 {currentLogs.length} 个日志文件
                                </Typography>
                            </LogHeader>

                            <List>
                                {currentLogs.map((log, index) => (
                                    <React.Fragment key={index}>
                                        <LogItem>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <StatusBadge label="日志文件" size="small" status="INFO" />
                                                        <Typography variant="subtitle1" fontWeight={500}>
                                                            {log.sn || '未命名日志文件'}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography variant="body2">
                                                            生成时间: {formatTimestamp(log.logFileDateTime)}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            文件大小: {Math.round(Math.random() * 500) + 100} KB
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                            文件路径: {log.logFileUrl}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                                <DownloadButton
                                                    variant="contained"
                                                    startIcon={<Download />}
                                                    href={log.logFileUrl}
                                                    component="a"
                                                    download
                                                >
                                                    下载日志
                                                </DownloadButton>
                                                <Typography variant="caption" color="textSecondary">
                                                    点击下载完整日志文件
                                                </Typography>
                                            </Box>
                                        </LogItem>
                                        {index < currentLogs.length - 1 && <Divider variant="middle" />}
                                    </React.Fragment>
                                ))}
                            </List>

                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f7fa', borderRadius: 2 }}>
                                <Typography variant="body2" fontWeight="bold" color="#2775EC" sx={{ mb: 1 }}>
                                    日志使用说明
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    • 日志文件通常在设备操作后5-10分钟内生成
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    • 点击"下载日志"按钮可获取完整日志文件
                                </Typography>
                                <Typography variant="body2">
                                    • 如遇下载问题，请检查设备网络连接状态
                                </Typography>
                            </Box>
                        </LogContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogDialogOpen(false)} color="primary">关闭</Button>
                </DialogActions>
            </LogDialog>
        </>
    )
}